import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { ExternalLink, Heart, BarChart3, Calendar, Globe, FileText } from "lucide-react";

const projects = [
  {
    title: "LoveLoopP - Dating & Chat App",
    description:
      "Modern dating application with chat system, matching features and user engagement tools.",
    link: "https://play.google.com/store/apps/details?id=com.loveloop.dating.loveloop",
    icon: Heart,
    image:
      "https://customer-assets.emergentagent.com/job_app-builder-4238/artifacts/4c1rggmb_Frame%2026%20%281%29.png",
    tags: ["Android", "Chat", "Firebase"],
  },
  {
    title: "Khata Master",
    description:
      "Smart financial management app for tracking daily transactions and account records.",
    link: "https://play.google.com/store/apps/details?id=com.khatamaster.khata.master",
    icon: BarChart3,
    image:
      "https://customer-assets.emergentagent.com/job_app-builder-4238/artifacts/44do6zn7_image%20%285%29.jpg",
    tags: ["Fintech", "Kotlin", "SQLite"],
  },
  {
    title: "FestMitra App",
    description:
      "Event Ticket Booking App – Discover & Book Events Easily.",
    link: "https://play.google.com/store/apps/details?id=com.demo.passbookdemo",
    icon: Calendar,
    image:
      "https://customer-assets.emergentagent.com/job_app-builder-4238/artifacts/6wxcaq6o_Frame%202.png",
    tags: ["Events", "Booking", "Android"],
  },
  {
    title: "Speak & Translate",
    description:
      "Advanced translation app with voice, text and image translation features.",
    link: "https://play.google.com/store/apps/details?id=com.translia.voice.translotic",
    icon: Globe,
    image:
      "https://customer-assets.emergentagent.com/job_app-builder-4238/artifacts/p7lncafs_logo%20-%2014%20%281%29.png",
    tags: ["Translation", "Voice", "ML"],
  },
  {
    title: "ScanCab: PDF Scanner",
    description:
      "Powerful PDF document scanner app that lets you scan documents, turn them into PDFs, and share them right away.",
    link: "https://play.google.com/store/apps/details?id=com.scan.scancab",
    icon: FileText,
    image:
      "https://customer-assets.emergentagent.com/job_app-builder-4238/artifacts/tz0fp04v_image%20%2854%29%20%281%29.png",
    tags: ["Scanner", "PDF", "Utility"],
  },
];

export const Projects = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      id="projects"
      ref={ref}
      className="py-24 sm:py-32 px-6 sm:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-4">
              Portfolio
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              Featured Applications
            </h2>
          </motion.div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, index) => (
              <motion.a
                key={project.title}
                href={project.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`project-card-${index}`}
                variants={itemVariants}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
                className="group bg-[#0C0C0C] border border-white/10 overflow-hidden hover:border-[#D4FF00] active:border-[#D4FF00] active:shadow-[0_0_30px_rgba(212,255,0,0.3)] focus:border-[#D4FF00] focus:shadow-[0_0_30px_rgba(212,255,0,0.3)] transition-all duration-300 block outline-none"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden bg-[#0a0a0a] flex items-center justify-center">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-32 h-32 object-contain rounded-2xl group-hover:scale-110 transition-all duration-500"
                  />
                  <div className="absolute top-4 right-4 w-10 h-10 bg-[#D4FF00] flex items-center justify-center text-black">
                    <project.icon size={20} />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-[#D4FF00] transition-colors flex items-center gap-2">
                    {project.title}
                    <ExternalLink size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  <p className="text-[#888888] text-sm leading-relaxed mb-4">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-white/5 text-[#888888] text-xs font-mono"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
